import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Tag }               from 'primereact/tag';
import { InputTextarea } from 'primereact/inputtextarea';
import { handleGetRequest } from '../../services/GetTemplate';
import { handlePutRequest } from '../../services/PutTemplate';
import { baseURL } from '../../Config';
import { Dialog } from 'primereact/dialog';
import Loader from '../../components/loader';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import '../Reports/ViewReport.css';
import './PermitViewSideBar.css';

function PermitViewSideBar({ onHideSidebar, editData, onActionPerformed }) {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageVisible, setImageVisible] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageName, setImageName] = useState(null);
    const [processingAction, setProcessingAction] = useState(false);
    const [rejectionComment, setRejectionComment] = useState('');
    const [showRejectionDialog, setShowRejectionDialog] = useState(false);
    
    const dispatch = useDispatch();
    const roleName = localStorage.getItem('roleName');

    // Handler for updating permit status (approve/reject)
    const handleUpdateStatus = (status) => async () => {
        try {
            setProcessingAction(true);
            // Create payload with id and isApproved flag based on status
            const payload = {
                id: editData,
                isApproved: status === 'Approved' ? true : false,
                comment: status === 'Rejected' ? rejectionComment : ''
            };
            
            // Call the new endpoint with the payload
            const response = await dispatch(handlePutRequest(payload, `/api/permit/status`));
            
            if (response?.success) {
                toast.success(`Permit has been ${status.toLowerCase()} successfully`);
                // Reset the comment state
                setRejectionComment('');
                // Hide dialog if shown
                setShowRejectionDialog(false);
                onActionPerformed();
                 setTimeout(() => {
                onHideSidebar();
            }, 100);
            } else {
                toast.error(`Failed to ${status.toLowerCase()} permit`);
            }
        } catch (error) {
            console.error(`Error ${status.toLowerCase()}ing permit:`, error);
            toast.error(`An error occurred while ${status.toLowerCase()}ing the permit`);
        } finally {
            setProcessingAction(false);
        }
    };
    
    // Handler for showing rejection dialog
    const showRejectDialog = () => {
        setShowRejectionDialog(true);
    };

    const fetchReportDataByID = async () => {
        try {
            if (editData) {
                setLoading(true);
                const response = await handleGetRequest(`/api/permit/${editData}`);
                if (response?.success) {
                    setReportData(response?.data[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Fetch report data when component mounts or editData changes
        fetchReportDataByID();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData]);

    if (loading) {
        return (
            <div className="flex justify-content-center align-items-center h-full">
                <i className="pi pi-spin pi-spinner text-3xl"></i>
            </div>
        );
    }

    return (
        <>
            {loading && (
                <div
                    className="fixed top-0 left-0 w-full h-full flex items-center justify-center"
                    style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(2px)',
                        zIndex: 1000
                    }}
                >
                    <Loader />
                </div>
            )}

            {imageUrl && (
                <Dialog
                    header={
                        <div className="header-content">
                            <h2>{imageName}</h2>
                            <button className="close-button" onClick={() => setImageVisible(false)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                    }
                    visible={imageVisible}
                    closable={false}
                    onHide={() => setImageVisible(false)}
                    className="image-dialog"
                    style={{ width: '898px', height: '722px' }}
                >
                    <img src={`${baseURL}${imageUrl}`} alt="" style={{ width: '773.51px', height: '551.12px', objectFit: 'contain' }} />
                </Dialog>
            )}
            
            {/* Rejection Reason Dialog */}
            <Dialog
                header={
                    <div className="header-content">
                        <h2>Rejection Reason</h2>
                        <button className="close-button" onClick={() => setShowRejectionDialog(false)}>
                            <i className="pi pi-times"></i>
                        </button>
                    </div>
                }
                visible={showRejectionDialog}
                closable={false}
                onHide={() => setShowRejectionDialog(false)}
                className="rejection-dialog"
                style={{ width: '500px' }}
                footer={
                    <div className="flex justify-content-end">
                        <Button 
                            label="Cancel" 
                            className="p-button-text" 
                            onClick={() => setShowRejectionDialog(false)} 
                        />
                        <Button 
                            label="Submit" 
                            className="p-button-secondary p-button-outlined" 
                                    style={{ 
                                        minWidth: '120px', 
                                        height: '40px',
                                        backgroundColor: '#FF556A', 
                                        border: 'none',
                                        color: 'white'
                                    }}
                            onClick={handleUpdateStatus('Rejected')}
                            disabled={!rejectionComment.trim() || processingAction}
                            loading={processingAction}
                        />
                    </div>
                }
            >
                <div className="p-field w-full">
                    <label htmlFor="rejectionComment" className="font-bold block mb-2">Please provide a reason for rejection:</label>
                    <InputTextarea
                        id="rejectionComment"
                        value={rejectionComment}
                        onChange={(e) => setRejectionComment(e.target.value)}
                        rows={5}
                        className="w-full"
                        placeholder="Enter rejection reason here..."
                        autoFocus
                    />
                </div>
            </Dialog>

            {/* New sidebar structure matching RenewRoutePermit */}
            <div
                className="permit-details-sidebar"
                style={{
                    height: '100vh', // Ensure the sidebar itself takes full viewport height
                    background: 'white' // Ensure sidebar has a background
                }}
            >
                {/* Header Section */}
                <div
                    className="sidebar-header"
                    style={{
                        padding: '1rem',
                        borderBottom: '1px solid #eee',
                        background: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <div className="header-content">
                        <h2>Permit Details</h2>
                    </div>
                    <Button icon="pi pi-times" onClick={onHideSidebar} className="p-button-rounded p-button-danger p-button-text p-button-sm" style={{ fontSize: '1rem' }} />
                </div>

                <div
                    className="p-4"
                    style={{
                        height: 'calc(100% - 60px)', /* Account for header height */
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingBottom: '80px', /* Extra space for button */
                        boxSizing: 'border-box'
                    }}
                >
                    <div className="section">
                        <h4 className="section-title">Contact Info</h4>
                        <div className="info-grid w-full">
                            <div className="info-item w-full">
                                <i className="pi pi-user" />
                                <strong>Applicant Name:</strong>
                                <span>{reportData?.name || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-id-card" />
                                <strong>CNIC Number:</strong>
                                <span>{reportData?.cnic || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-phone" />
                                <strong>Phone Number:</strong>
                                <span>{reportData?.mobileNum || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-map-marker" />
                                <strong>Address:</strong>
                                <span>{reportData?.address || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-users" />
                                <strong>Applicant Type:</strong>
                                <span>{reportData?.applicantType || ''}</span>
                            </div>
                        </div>
                    </div>

                    <div className="section">
                        <h4 className="section-title">Vehicle Details</h4>
                        <div className="info-grid w-full">
                            <div className="info-item w-full">
                                <i className="pi pi-tag" />
                                <strong>Vehicle Category:</strong>
                                <span>{reportData?.vehicleTypeName || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-id-card" />
                                <strong>Registration Number:</strong>
                                <span>{reportData?.regNum || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-cog" />
                                <strong>Engine Number:</strong>
                                <span>{reportData?.engineNum || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-hashtag" />
                                <strong>Chassis Number:</strong>
                                <span>{reportData?.chassisNum || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-calendar" />
                                <strong>Model:</strong>
                                <span>{reportData?.model || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-building" />
                                <strong>Manufacturer:</strong>
                                <span>{reportData?.maker || ''}</span>
                            </div>
                        </div>
                    </div>

                    <div className="section documents-section">
                        <h4 className="section-title">Documents Attached</h4>
                        <div className="document-grid w-full">
                            <div className="info-item w-full">
                                <i className="pi pi-file-pdf" />
                                <strong>Vehicle Registration Book:</strong>
                                {reportData?.regBook ? (
                                    <span
                                        className="document-link"
                                        onClick={() => {
                                            setImageVisible(true);
                                            setImageUrl(reportData?.regBook);
                                            setImageName('Vehicle Registration Book');
                                        }}
                                    >
                                        View Document
                                    </span>
                                ) : (
                                    <span>Not Available</span>
                                )}
                            </div>
                            {/* <div className="info-item w-full">
                                <i className="pi pi-file" />
                                <strong>Token:</strong>
                                {reportData?.token ? (
                                    <span
                                        className="document-link"
                                        onClick={() => {
                                            setImageVisible(true);
                                            setImageUrl(reportData?.token);
                                            setImageName('Token');
                                        }}
                                    >
                                        View Document
                                    </span>
                                ) : (
                                    <span>Not Available</span>
                                )}
                            </div> */}
                            <div className="info-item w-full">
                                <i className="pi pi-file-excel" />
                                <strong>Fitness Certificate:</strong>
                                {reportData?.fitness ? (
                                    <span
                                        className="document-link"
                                        onClick={() => {
                                            setImageVisible(true);
                                            setImageUrl(reportData?.fitness);
                                            setImageName('Fitness Certificate');
                                        }}
                                    >
                                        View Document
                                    </span>
                                ) : (
                                    <span>Not Available</span>
                                )}
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-file-pdf" />
                                <strong>Affidavit:</strong>
                                {reportData?.affidavit ? (
                                    <span
                                        className="document-link"
                                        onClick={() => {
                                            setImageVisible(true);
                                            setImageUrl(reportData?.affidavit);
                                            setImageName('Affidavit');
                                        }}
                                    >
                                        View Document
                                    </span>
                                ) : (
                                    <span>Not Available</span>
                                )}
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-id-card" />
                                <strong>ID Card Front:</strong>
                                {reportData?.nicFront ? (
                                    <span
                                        className="document-link"
                                        onClick={() => {
                                            setImageVisible(true);
                                            setImageUrl(reportData?.nicFront);
                                            setImageName('ID Card Front');
                                        }}
                                    >
                                        View Document
                                    </span>
                                ) : (
                                    <span>Not Available</span>
                                )}
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-id-card" />
                                <strong>ID Card Back:</strong>
                                {reportData?.nicBack ? (
                                    <span
                                        className="document-link"
                                        onClick={() => {
                                            setImageVisible(true);
                                            setImageUrl(reportData?.nicBack);
                                            setImageName('ID Card Back');
                                        }}
                                    >
                                        View Document
                                    </span>
                                ) : (
                                    <span>Not Available</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="section">
                        <h4 className="section-title">Request Details</h4>
                        <div className="info-grid w-full">
                            <div className="info-item w-full">
                                <i className="pi pi-calendar" />
                                <strong>Request Date:</strong>
                                <span>{reportData?.reqDate || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-check-circle" />
                                <strong>Request Status:</strong>
                                <Tag
                                    value={reportData?.reqStatus || ''}
                                    severity={
                                        reportData?.reqStatus === 'Approved'
                                            ? 'success'
                                            : reportData?.reqStatus === 'Rejected'
                                            ? 'danger'
                                            : 'warning'
                                    }
                                    style={{ marginLeft: '5px' }}
                                />
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-sync" />
                                <strong>Renewal Status:</strong>
                                <span>{reportData?.isRenewed === true ? 'Yes' : 'No' || ''}</span>
                            </div>
                        </div>
                    </div>

                    <div className="section">
                        <h4 className="section-title">Permit Details</h4>
                        <div className="info-grid w-full">
                            <div className="info-item w-full">
                                <i className="pi pi-map" />
                                <strong>Route Number:</strong>
                                <span>{reportData?.routeNumber || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-shield" />
                                <strong>Fitness Certificate:</strong>
                                <span>{reportData?.fitnessCFX || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-ticket" />
                                <strong>Permit Number:</strong>
                                <span>{reportData?.permitNum || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-calendar-plus" />
                                <strong>Issue Date:</strong>
                                <span>{reportData?.permitIssue || ''}</span>
                            </div>
                            <div className="info-item w-full">
                                <i className="pi pi-calendar-times" />
                                <strong>Expiry Date:</strong>
                                <span>{reportData?.permitExp || ''}</span>
                            </div>
                        </div>
                    </div>

                    <div className="section">
                        <h4 className="section-title">Fee Details</h4>
                        <div className="fee-details w-full">
                            <div className="fee-item w-full">
                                <div>
                                    <i className="pi pi-dollar" />
                                    <strong>Permit Fee:</strong>
                                </div>
                                <span>{reportData?.fee || 'N/A'} Rs.</span>
                            </div>
                            <div className="fee-item w-full">
                                <div>
                                    <i className="pi pi-exclamation-circle" />
                                    <strong>Permit Fine:</strong>
                                </div>
                                <span>{reportData?.fine || 'N/A'} Rs.</span>
                            </div>
                            <div className="fee-item total w-full">
                                <div>
                                    <i className="pi pi-money-bill" />
                                    <strong>Total Permit Fee:</strong>
                                </div>
                                <span>{reportData?.totalAmount || 'N/A'} Rs.</span>
                            </div>
                            <div className="fee-item w-full">
                                <div>
                                    <i className="pi pi-wallet" />
                                    <strong>Payment Method:</strong>
                                </div>
                                <span>{reportData?.paymentMethod || 'N/A'}</span>
                            </div>
                            <div className="fee-item w-full">
                                <div>
                                    <i className="pi pi-check-square" />
                                    <strong>Payment Status:</strong>
                                </div>
                                <span>{reportData?.paymentStatus || 'N/A'}</span>
                            </div>
                            <div className="fee-item w-full">
                                <div>
                                    <i className="pi pi-id-card" />
                                    <strong>Transaction ID:</strong>
                                </div>
                                <span>{reportData?.trnxId || 'N/A'}</span>
                            </div>
                            <div className="fee-item w-full">
                                <div>
                                    <i className="pi pi-calendar-times" />
                                    <strong>Transaction Date:</strong>
                                </div>
                                <span>{reportData?.trnxStamp || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                    <div
                        className="mt-5 pt-3 border-top-1 border-300"
                        style={{
                            marginTop: '2rem',
                            paddingTop: '1rem',
                            paddingBottom: '1rem',
                            background: 'white',
                            textAlign: 'center'
                        }}
                    >
                        {roleName === 'Deputy Director' && reportData?.reqStatus === 'Pending' ? (
                            <div className="flex justify-content-center gap-3 mt-4">
                                <Button 
                                    label="Reject" 
                                    className="p-button-secondary p-button-outlined" 
                                    style={{ 
                                        minWidth: '120px', 
                                        height: '40px',
                                        backgroundColor: '#FF556A', 
                                        border: 'none',
                                        color: 'white'
                                    }}
                                    onClick={showRejectDialog}
                                    disabled={processingAction}
                                />
                                <Button 
                                    label="Approve" 
                                    className="p-button-primary" 
                                    style={{ 
                                        minWidth: '120px', 
                                        height: '40px',
                                        backgroundColor: '#3A974C', 
                                        border: 'none'
                                    }}
                                    onClick={handleUpdateStatus('Approved')}
                                    disabled={processingAction}
                                    loading={processingAction}
                                />
                            </div>
                        ) : (
                            <Button 
                                label="Close" 
                                className="p-button-secondary p-button-outlined" 
                                style={{ minWidth: '120px', height: '40px' }} 
                                
                                onClick={onHideSidebar} 
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default PermitViewSideBar;
